import { Route, RouteState, Activity, TrackPoint, Position } from "../types";
import { calculateDistance } from "../utils/geoUtils";
import debugx from "debugx";
import { getOPFSRoot } from "../utils/opfs-wrapper";

const debug = debugx("bikedash:route");

const STORAGE_KEY = "bikedash_active_route";
const SAVED_ROUTES_KEY = "bikedash_saved_routes";

class RouteService {
  private static instance: RouteService;
  private state: RouteState = {
    isActive: false,
    routeName: null,
    routeId: null,
    totalDistance: 0,
    estimatedTime: 0,
    startTime: null,
    geometry: null,
    isSaved: false,
  };

  private savedRoutes: Route[] = [];
  private routesLoaded: boolean = false;

  private listeners: Set<(state: RouteState) => void> = new Set();
  private savedRoutesListeners: Set<() => void> = new Set();

  private constructor() {
    debug("RouteService initialized");
    this.loadState();
    this.loadSavedRoutes().then(() => {
      this.routesLoaded = true;
      this.notifySavedRoutesListeners();
    });
  }

  private loadState() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.state = { ...this.state, ...parsed };
        debug("Loaded route state from localStorage: %s", parsed.routeName);
      }
    } catch (error) {
      debug.error("Failed to load route state:", error);
    }
  }

  private async loadSavedRoutes() {
    try {
      const root = await getOPFSRoot();
      const routesDir = await root.getDirectoryHandle("saved_routes", {
        create: true,
      });

      this.savedRoutes = [];
      // @ts-ignore - values() exists on FileSystemDirectoryHandle but TypeScript may not recognize it
      for await (const entry of routesDir.values()) {
        if (entry.kind === "file" && entry.name.endsWith(".json")) {
          const fileHandle = await routesDir.getFileHandle(entry.name);
          const file = await fileHandle.getFile();
          const text = await file.text();
          const route = JSON.parse(text);
          this.savedRoutes.push(route);
        }
      }
      debug("Loaded %d saved routes from OPFS", this.savedRoutes.length);
    } catch (error) {
      debug.error("Failed to load saved routes:", error);
      console.error("Failed to load saved routes:", error);
    }
  }

  private saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      debug("Saved route state to localStorage");
    } catch (error) {
      debug.error("Failed to save route state:", error);
    }
  }

  private async saveSavedRoutes() {
    try {
      const root = await getOPFSRoot();
      const routesDir = await root.getDirectoryHandle("saved_routes", {
        create: true,
      });

      // Save all routes to OPFS
      for (const route of this.savedRoutes) {
        const fileName = `route_${route.id}.json`;
        const fileHandle = await routesDir.getFileHandle(fileName, {
          create: true,
        });
        const writable = await fileHandle.createWritable();
        await writable.write(JSON.stringify(route));
        await writable.close();
      }
      debug("Saved %d routes to OPFS", this.savedRoutes.length);
    } catch (error) {
      debug.error("Failed to save routes:", error);
      console.error("Failed to save routes:", error);
    }
  }

  static getInstance(): RouteService {
    if (!RouteService.instance) {
      RouteService.instance = new RouteService();
    }
    return RouteService.instance;
  }

  getState(): RouteState {
    return { ...this.state };
  }

  startRoute(options: {
    routeName: string;
    routeId: string;
    totalDistance: number;
    estimatedTime?: number;
    geometry?: any[];
    centerLat?: number;
    centerLon?: number;
  }) {
    debug("Starting route: %s", options.routeName);
    // Check if this route is already saved
    const isSaved = this.savedRoutes.some((r) => r.id === options.routeId);

    this.state = {
      isActive: true,
      routeName: options.routeName,
      routeId: options.routeId,
      totalDistance: options.totalDistance,
      estimatedTime: options.estimatedTime || 0,
      startTime: Date.now(),
      geometry: options.geometry || null,
      isSaved: isSaved,
      centerLat: options.centerLat,
      centerLon: options.centerLon,
    };
    this.saveState();
    this.notifyListeners();
  }

  stopRoute() {
    debug("Stopping route");
    this.state = {
      isActive: false,
      routeName: null,
      routeId: null,
      totalDistance: 0,
      estimatedTime: 0,
      startTime: null,
      geometry: null,
      isSaved: false,
    };
    this.saveState();
    this.notifyListeners();
  }

  async saveCurrentRoute() {
    if (!this.state.isActive || !this.state.geometry) {
      debug.warn("Cannot save route: no active route");
      return;
    }

    // Use the centerLat/centerLon from state (passed from search result)
    // or calculate if not available
    let centerLat = this.state.centerLat || 0;
    let centerLon = this.state.centerLon || 0;

    if (!this.state.centerLat || !this.state.centerLon) {
      // Fallback: calculate from geometry
      let pointCount = 0;
      for (const elem of this.state.geometry) {
        if (elem.geometry?.coordinates) {
          for (const coord of elem.geometry.coordinates) {
            centerLon += coord[0];
            centerLat += coord[1];
            pointCount++;
          }
        }
      }
      if (pointCount > 0) {
        centerLat /= pointCount;
        centerLon /= pointCount;
      }
    }

    const savedRoute: Route = {
      id: this.state.routeId!,
      name: this.state.routeName!,
      totalDistance: this.state.totalDistance,
      geometry: this.state.geometry || undefined,
      createdAt: Date.now(),
      centerLat,
      centerLon,
      waypoints: [], // TODO: populate from actual waypoints if available
    };

    // Check if already saved
    const existingIndex = this.savedRoutes.findIndex(
      (r) => r.id === savedRoute.id,
    );
    if (existingIndex >= 0) {
      this.savedRoutes[existingIndex] = savedRoute;
    } else {
      this.savedRoutes.push(savedRoute);
    }

    await this.saveSavedRoutes();
    this.state.isSaved = true;
    this.saveState();
    this.notifyListeners();
    this.notifySavedRoutesListeners();
    debug(
      "Saved route: %s at %.4f, %.4f",
      savedRoute.name,
      centerLat,
      centerLon,
    );
  }

  async unsaveCurrentRoute() {
    if (!this.state.routeId) return;

    this.savedRoutes = this.savedRoutes.filter(
      (r) => r.id !== this.state.routeId,
    );

    // Delete from OPFS
    try {
      const root = await getOPFSRoot();
      const routesDir = await root.getDirectoryHandle("saved_routes", {
        create: true,
      });
      const fileName = `route_${this.state.routeId}.json`;
      await routesDir.removeEntry(fileName);
    } catch (error) {
      debug.error("Failed to delete route file:", error);
      console.error("Failed to delete route file:", error);
    }

    this.state.isSaved = false;
    this.saveState();
    this.notifyListeners();
    this.notifySavedRoutesListeners();
    debug("Unsaved route: %s", this.state.routeName);
  }

  async renameCurrentRoute(newName: string) {
    if (!this.state.isActive || !this.state.isSaved) {
      debug.warn("Cannot rename: route not active or not saved");
      return;
    }

    this.state.routeName = newName;
    this.saveState();

    // Update in saved routes
    const savedRoute = this.savedRoutes.find(
      (r) => r.id === this.state.routeId,
    );
    if (savedRoute) {
      savedRoute.name = newName;
      await this.saveSavedRoutes();
    }

    this.notifyListeners();
    debug("Renamed route to: %s", newName);
  }

  async createRouteFromActivity(
    activity: Activity,
    trackPoints: TrackPoint[],
    save: boolean = true,
  ): Promise<Route> {
    if (!trackPoints || trackPoints.length === 0) {
      throw new Error("No track points available for this activity");
    }

    // Generate route ID from activity ID
    const routeId = `route_from_${activity.id}`;

    // Use activity name directly as route name
    const routeName =
      activity.title ||
      `Activity ${new Date(activity.startTime).toLocaleDateString()}`;

    // Convert track points to way segments (split on distance or time gaps)
    const TIME_GAP_THRESHOLD = 120000; // 2 minutes in milliseconds
    const DISTANCE_GAP_THRESHOLD = 50; // 50 meters
    const geometry: any[] = [];
    let currentSegment: { lat: number; lon: number }[] = [];
    let segmentId = 1;
    let minLat = Infinity,
      maxLat = -Infinity;
    let minLon = Infinity,
      maxLon = -Infinity;

    for (let i = 0; i < trackPoints.length; i++) {
      const point = trackPoints[i];
      const lat = point.position.lat!;
      const lon = point.position.lng!;

      // Check for gaps to split segments
      if (i > 0 && currentSegment.length > 0) {
        const prevPoint = trackPoints[i - 1];
        let shouldStartNewSegment = false;

        // Check time gap
        const timeDiff =
          point.position.timestamp - prevPoint.position.timestamp;
        if (timeDiff > TIME_GAP_THRESHOLD) {
          shouldStartNewSegment = true;
          debug("Route segment time gap: %d seconds", timeDiff / 1000);
        }

        // Check distance gap
        const distance = calculateDistance(
          prevPoint.position.lat!,
          prevPoint.position.lng!,
          lat,
          lon,
        );
        if (distance > DISTANCE_GAP_THRESHOLD) {
          shouldStartNewSegment = true;
          debug("Route segment distance gap: %dm", distance);
        }

        if (shouldStartNewSegment) {
          // Save current segment as a way
          if (currentSegment.length > 1) {
            const segBounds = calculateBounds(currentSegment);
            geometry.push({
              type: "way",
              id: `activity_segment_${segmentId++}`,
              bounds: segBounds,
              nodes: currentSegment.map((_, idx) => `node_${segmentId}_${idx}`),
              geometry: currentSegment, // Array of {lat, lon} objects
              tags: {
                highway: "path",
                name: routeName,
                surface: "paved",
                bicycle: "yes",
                foot: "yes",
              },
              _debug_connected: true,
              _debug_primary: true,
              _debug_same_name: true,
              _debug_is_connector: false,
              _debug_connected_route: true,
            });
          }
          currentSegment = [];
        }
      }

      // Add point to current segment
      currentSegment.push({ lat, lon });

      // Update overall bounds
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
    }

    // Add final segment
    if (currentSegment.length > 1) {
      const segBounds = calculateBounds(currentSegment);
      geometry.push({
        type: "way",
        id: `activity_segment_${segmentId}`,
        bounds: segBounds,
        nodes: currentSegment.map((_, idx) => `node_${segmentId}_${idx}`),
        geometry: currentSegment, // Array of {lat, lon} objects
        tags: {
          highway: "path",
          name: routeName,
          surface: "paved",
          bicycle: "yes",
          foot: "yes",
        },
        _debug_connected: true,
        _debug_primary: true,
        _debug_same_name: true,
        _debug_is_connector: false,
        _debug_connected_route: true,
      });
    }

    // Helper function to calculate bounds for a segment
    function calculateBounds(segment: { lat: number; lon: number }[]) {
      let minLat = Infinity,
        maxLat = -Infinity;
      let minLon = Infinity,
        maxLon = -Infinity;
      for (const point of segment) {
        minLat = Math.min(minLat, point.lat);
        maxLat = Math.max(maxLat, point.lat);
        minLon = Math.min(minLon, point.lon);
        maxLon = Math.max(maxLon, point.lon);
      }
      return { minlat: minLat, minlon: minLon, maxlat: maxLat, maxlon: maxLon };
    }

    // Calculate center from bounds
    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    // Get start and end positions for waypoints
    const startPosition: [number, number] = [
      trackPoints[0].position.lng!,
      trackPoints[0].position.lat!,
    ];
    const endPosition: [number, number] = [
      trackPoints[trackPoints.length - 1].position.lng!,
      trackPoints[trackPoints.length - 1].position.lat!,
    ];

    debug("Built %d way segments from track points", geometry.length);

    // Create the route object using geometry field instead of trackPoints
    const route: Route = {
      id: routeId,
      name: routeName,
      description:
        activity.description ||
        `Created from activity on ${new Date(activity.startTime).toLocaleString()}`,
      waypoints: [startPosition, endPosition], // Start and end as waypoints
      geometry, // Use geometry field with way objects
      totalDistance: activity.distance,
      estimatedTime: activity.endTime
        ? Math.round(
            (activity.endTime - activity.startTime - activity.pausedDuration) /
              1000,
          ) // Convert to seconds
        : Math.round(activity.distance / 5), // Estimate 5 m/s if activity not complete
      createdAt: Date.now(),
      tags: activity.tags,
      centerLat,
      centerLon,
      sourceActivityId: activity.id, // Reference back to original activity
    };

    if (save) {
      // Check if route already exists and update or add
      const existingIndex = this.savedRoutes.findIndex((r) => r.id === routeId);
      if (existingIndex >= 0) {
        this.savedRoutes[existingIndex] = route;
        debug("Updated existing route from activity: %s", activity.id);
      } else {
        this.savedRoutes.push(route);
        debug("Created new route from activity: %s", activity.id);
      }

      // Save routes to OPFS
      await this.saveSavedRoutes();

      // Notify listeners of saved routes change
      this.notifySavedRoutesListeners();

      debug(
        "Saved activity %s as route %s with %d track points",
        activity.id,
        routeId,
        trackPoints.length,
      );
    } else {
      debug(
        "Created temporary route from activity %s with %d track points",
        activity.id,
        trackPoints.length,
      );
    }

    return route;
  }

  getSavedRoutes(): Route[] {
    return [...this.savedRoutes];
  }

  searchSavedRoutes(query: string): Route[] {
    if (!query) return this.getSavedRoutes();

    const lowerQuery = query.toLowerCase();
    return this.savedRoutes.filter((route) =>
      route.name.toLowerCase().includes(lowerQuery),
    );
  }

  subscribe(listener: (state: RouteState) => void): () => void {
    this.listeners.add(listener);
    // Call immediately with current state
    listener(this.getState());
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeSavedRoutes(listener: () => void): () => void {
    this.savedRoutesListeners.add(listener);
    // Call immediately
    listener();
    // Return unsubscribe function
    return () => {
      this.savedRoutesListeners.delete(listener);
    };
  }

  private notifyListeners() {
    const state = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (error) {
        debug.error("Listener error:", error);
      }
    });
  }

  private notifySavedRoutesListeners() {
    this.savedRoutesListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        debug.error("Saved routes listener error:", error);
      }
    });
  }
}

export function getRouteService(): RouteService {
  return RouteService.getInstance();
}
